import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import {
  Camera, CheckCircle, Warning, Question, ArrowRight, Phone, SpinnerGap, ShieldCheck, ArrowCounterClockwise, UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SITE_URL } from "@/seo/data";

const VERDICTS = {
  reparierbar: {
    icon: CheckCircle,
    box: "border-emerald-200 bg-emerald-50",
    badge: "bg-emerald-600",
    title: "Gute Nachricht: wahrscheinlich reparierbar",
    sub: "Eine Steinschlag-Reparatur dauert bei uns meist nur 30–45 Minuten. Bei Teilkasko ist sie je nach Vertrag oft ohne Selbstbeteiligung möglich.",
  },
  austausch: {
    icon: Warning,
    box: "border-amber-200 bg-amber-50",
    badge: "bg-amber-600",
    title: "Vermutlich ist ein Scheibentausch nötig",
    sub: "Keine Sorge — wir übernehmen auf Wunsch die Abwicklung mit Ihrer Versicherung und erklären Ihnen alle nächsten Schritte.",
  },
  unklar: {
    icon: Question,
    box: "border-slate-200 bg-slate-50",
    badge: "bg-slate-600",
    title: "Nicht eindeutig erkennbar",
    sub: "Versuchen Sie ein schärferes Foto bei Tageslicht, nah am Schaden — oder melden Sie den Schaden direkt, wir schauen es uns an.",
  },
  kein_glasschaden: {
    icon: Question,
    box: "border-slate-200 bg-slate-50",
    badge: "bg-slate-600",
    title: "Kein Glasschaden erkennbar",
    sub: "Auf dem Foto ist kein Scheibenschaden zu erkennen. Bei Fragen erreichen Sie uns jederzeit telefonisch.",
  },
};

async function resizeImage(file, maxDim = 1280) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise((res) => canvas.toBlob((b) => res(b || file), "image/jpeg", 0.85));
  } catch {
    return file;
  }
}

export default function SchnellCheck() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const blobRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async (file) => {
    if (!file) return;
    setResult(null);
    const blob = await resizeImage(file);
    blobRef.current = blob;
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(blob);
    });
    setLoading(true);
    const fd = new FormData();
    fd.append("file", blob, "schaden.jpg");
    try {
      const { data } = await api.post("/ai/damage-check", fd);
      setResult(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Analyse fehlgeschlagen — bitte erneut versuchen");
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    blobRef.current = null;
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  const goToClaim = async () => {
    setHandoffLoading(true);
    const handoff = { result };
    try {
      if (blobRef.current) {
        const fd = new FormData();
        fd.append("file", blobRef.current, "schnellcheck-schaden.jpg");
        fd.append("category", "damage_detail");
        const { data } = await api.post("/upload", fd);
        handoff.file = data;
      }
    } catch {
      /* Foto-Übernahme optional — Meldung geht trotzdem weiter */
    }
    try {
      sessionStorage.setItem("schnellcheck_handoff", JSON.stringify(handoff));
    } catch {
      /* noop */
    }
    setHandoffLoading(false);
    navigate("/schaden-melden");
  };

  const V = result ? VERDICTS[result.verdict] || VERDICTS.unklar : null;

  return (
    <div className="min-h-screen bg-slate-50 pb-44 md:pb-24">
      <SeoHead
        title="KI-Schnell-Check: Steinschlag-Foto sofort prüfen lassen | Servus Autoglas"
        description="Foto vom Steinschlag hochladen und in Sekunden eine kostenlose KI-Ersteinschätzung erhalten: reparierbar oder Austausch nötig? Unverbindlich für Ludwigshafen, Mannheim & Umgebung."
        canonical={`${SITE_URL}/schnell-check`}
      />
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="schnellcheck-page">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
            <Camera size={14} weight="fill" /> Kostenlos & unverbindlich
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            Steinschlag? <span className="text-blue-600">Foto genügt.</span>
          </h1>
          <p className="mt-4 text-slate-600 max-w-lg mx-auto">
            Laden Sie ein Foto Ihres Scheibenschadens hoch — unsere KI gibt Ihnen in Sekunden eine erste
            Einschätzung, ob eine Reparatur oder ein Austausch wahrscheinlich ist.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-2 text-center text-xs text-slate-500">
          {["1 · Foto aufnehmen", "2 · KI analysiert", "3 · Einschätzung erhalten"].map((s) => (
            <div key={s} className="rounded-xl bg-white border border-slate-200 py-2.5 px-1 font-medium">{s}</div>
          ))}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => analyze(e.target.files?.[0])}
          data-testid="schnellcheck-file-input"
        />

        {!preview && (
          <button
            onClick={() => inputRef.current?.click()}
            className="mt-6 w-full rounded-3xl border-2 border-dashed border-blue-300 bg-blue-50/50 hover:bg-blue-50 transition-colors p-10 flex flex-col items-center gap-3"
            data-testid="schnellcheck-upload-btn"
          >
            <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center">
              <Camera size={32} weight="fill" color="white" />
            </div>
            <div className="font-semibold text-slate-900 text-lg">Foto aufnehmen oder hochladen</div>
            <div className="text-sm text-slate-500">JPG, PNG oder WEBP · max. 10 MB</div>
          </button>
        )}

        {preview && (
          <div className="mt-6 rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm">
            <img src={preview} alt="Hochgeladenes Schadenfoto" className="w-full max-h-80 object-contain bg-slate-900" />
          </div>
        )}

        {loading && (
          <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-center gap-3" data-testid="schnellcheck-loading">
            <SpinnerGap size={24} className="animate-spin text-blue-600 shrink-0" />
            <div className="text-sm text-blue-900 font-medium">Die KI analysiert Ihr Foto — einen Moment bitte…</div>
          </div>
        )}

        {result && V && (
          <div className={`mt-6 rounded-3xl border p-6 ${V.box}`} data-testid="schnellcheck-result">
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-xl ${V.badge} flex items-center justify-center shrink-0`}>
                <V.icon size={24} weight="fill" color="white" />
              </div>
              <div>
                <h2 className="font-bold text-slate-900 text-lg leading-snug" data-testid="schnellcheck-verdict">{V.title}</h2>
                {result.damage_type && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    Erkannt: {result.damage_type} · Einschätzungs-Sicherheit: {result.confidence}
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-700">{V.sub}</p>
            {result.reasons?.length > 0 && (
              <ul className="mt-4 space-y-1.5">
                {result.reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle size={16} weight="fill" className="text-slate-400 mt-0.5 shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            )}
            {result.recommendation && <p className="mt-3 text-sm font-medium text-slate-800">{result.recommendation}</p>}

            <div className="mt-5 grid gap-2.5">
              <Button
                onClick={goToClaim}
                disabled={handoffLoading}
                className="h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
                data-testid="schnellcheck-report-cta"
              >
                {handoffLoading ? (
                  <>
                    <SpinnerGap size={18} className="mr-2 animate-spin" /> Foto wird übernommen…
                  </>
                ) : (
                  <>
                    Jetzt Schaden melden — Foto wird übernommen <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
              <div className="grid grid-cols-2 gap-2.5">
                <a
                  href="tel:+49621527429"
                  className="flex items-center justify-center gap-2 h-11 rounded-xl bg-slate-900 text-white text-sm font-semibold"
                  data-testid="schnellcheck-call-cta"
                >
                  <Phone size={16} weight="fill" /> Anrufen
                </a>
                <Button variant="outline" onClick={reset} className="h-11 rounded-xl" data-testid="schnellcheck-retry">
                  <ArrowCounterClockwise size={16} className="mr-1.5" /> Neues Foto
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 flex gap-3 text-xs text-slate-500">
          <ShieldCheck size={18} className="text-slate-400 shrink-0 mt-0.5" />
          <p>
            Unverbindliche KI-Ersteinschätzung ohne Gewähr — die finale Beurteilung erfolgt kostenlos durch unsere
            Techniker in der Werkstatt. Ihr Foto wird nur für die Analyse verwendet und nicht dauerhaft gespeichert.
          </p>
        </div>

        {!preview && (
          <button
            onClick={() => inputRef.current?.click()}
            className="mt-4 w-full text-sm text-blue-600 font-medium inline-flex items-center justify-center gap-1.5 hover:underline"
            data-testid="schnellcheck-upload-link"
          >
            <UploadSimple size={16} /> Vorhandenes Foto aus der Galerie wählen
          </button>
        )}
      </main>
    </div>
  );
}
